import React, { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import getConfig from "next/config";
import { isMobile } from "react-device-detect";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";

const { publicRuntimeConfig } = getConfig();

const StyledGroup = styled(Form.Group)`
  position: relative;

  padding-left: 0;
  padding-right: 0;

  ${({ withLabel }) => withLabel && css`
    padding-top: 24px;
  `};
`;

const StyledLabel = styled(Form.Label)`
  position: absolute;
  left: 0;
  top: 0;
  font-family: 'LatoBold';
  font-size: 12px;
  line-height: 24px;
  text-transform: uppercase;
  white-space: nowrap;
  color: #078FF1;

  span {
    font-size: 16px;
    color: #C61F0C;
  }
`;

const StyledControl = styled(Form.Control)`
  appearance: unset;

  width: 100%;
  min-height: 52px;

  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;

  font-family: 'LatoRegular';
  font-size: 14px;
  line-height: 24px;
  color: ${hexToRGBA('#1B3858', 0.5)};

  border: none;
  border-radius: 7px;

  background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/caret_down.svg)`};
  background-repeat: no-repeat;
  background-origin: content-box;
  background-position-x: right;
  background-position-y: center;
  background-color: ${hexToRGBA('#078FF1', 0.1)};

  ${({ isValid }) => isValid && css`
    border: 1px solid #198754;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_success.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  ${({ isInvalid }) => isInvalid && css`
    border: 1px solid #DC3545;
    background-image: ${`url(${publicRuntimeConfig.basePath}/icons/V2/validation_failure.svg)`};
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem) !important;
  `};

  &:focus-visible {
    outline: none;
  }
`;

const StyledFeedback = styled(Form.Control.Feedback)`

`;

function Select({
  id,
  hint,
  label,
  emptyLabel,
  placeholder,
  required,
  noBottomSpace,
  validated,
  maxLength,
  defaultInvalidMessage = '',
  isInvalid,
  value = '',
  options = [],
  onChange,
  ...restProps
}) {
  const handleChange = useCallback((event) => {
    onChange && onChange(event);
  }, [onChange]);

  const overlayTriggerProps = useMemo(() => {
    const props = {
      placement: isMobile ? "top" : "right-start",
      delay: { show: 0, hide: 0 },
      trigger: ["hover", "focus"],
      overlay: (
        <Tooltip id={`tooltip-${id}`} className="hintTooltip">
          {hint}
        </Tooltip>
      ),
    };

    if (!hint || isMobile) {
      props.show = false;
    }

    return props;
  }, [id, hint]);

  const valid = !required && validated && !isInvalid || value && validated && !isInvalid;
  const invalid = isInvalid || (required && !value && validated);

  return (
    <OverlayTrigger {...overlayTriggerProps}>
      <StyledGroup
        controlId={id} 
        className={invalid ? 'custom-control-invalid' : ''}
        withLabel={Boolean(label || emptyLabel)}
        noBottomSpace={noBottomSpace}
      >
        <StyledLabel>
          {label}
          {!emptyLabel && label && required && (<span>&nbsp;*</span>)}
        </StyledLabel>
        <StyledControl
          as="select"
          className="form-select"
          name={id}
          required={required}
          maxLength={maxLength}
          value={value}
          onChange={handleChange}
          isValid={valid}
          isInvalid={invalid}
          {...restProps}
        >
          <option value="" selected>
            {placeholder}
          </option>
          {options.map(({ title, value }) => (
            <option key={value} value={value}>
              {title}
            </option>
          ))}
        </StyledControl>
        <StyledFeedback type="invalid">
          {defaultInvalidMessage}
        </StyledFeedback>
      </StyledGroup>
    </OverlayTrigger>
  );
}

export default Select;
